import { BlurView } from 'expo-blur';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { Modal, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useFonts } from '../../utils/fontLoader';

const MobileHeader = ({
  logo: LogoComponent,
  navLinks = [],
  onLogout,
  onSearchPress,
  onSharePress,
  onLogoPress,
  onAddCarPress,
  variant = 'default', // 'default' | 'dashboard' - dashboard hides extra icons
  isDark = false, // when true, header uses dark (transparent/blurred dark) bg
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const { fontsLoaded } = useFonts();
  const router = useRouter();

  if (!fontsLoaded) return null;

  const handlePress = (item) => {
    if (item.onPress) {
      item.onPress();
    } else if (item.target) {
      router.push(item.target);
    }
    setMenuVisible(false);
  };

  const isDashboard = variant === 'dashboard';
  const useDarkTheme = isDark || isDashboard;
  const containerStyles = [
    isDashboard ? styles.dashboardHeader : styles.header,
    Platform.OS === 'ios' ? {} : (useDarkTheme ? styles.androidDashboardHeader : styles.androidHeader),
  ];

  const HeaderContent = () => (
    <View style={styles.headerContent}>
      {LogoComponent &&
        (onLogoPress ? (
          <TouchableOpacity onPress={onLogoPress}>
            <LogoComponent width={100} height={28} />
          </TouchableOpacity>
        ) : (
          <View>
            <LogoComponent width={100} height={28} />
          </View>
        ))}
      <View style={{ flex: 1 }} />
      <View style={styles.rightHeader}>
        {/* Add a Car button for dashboard */}
        {isDashboard && onAddCarPress && (
          <TouchableOpacity onPress={onAddCarPress} style={styles.addCarButton}>
            <Icon name="plus" size={14} color="#FFFFFF" />
            <Text style={styles.addCarButtonText}>Add a Car</Text>
          </TouchableOpacity>
        )}
        {/* Dashboard variant only shows hamburger menu */}
        {!isDashboard && onSearchPress && (
          <TouchableOpacity onPress={onSearchPress} style={styles.menuButton}>
            <Icon name="magnify" size={28} color={useDarkTheme ? '#FFFFFF' : '#0E0E0E'} />
          </TouchableOpacity>
        )}
        {!isDashboard && onSharePress && (
          <TouchableOpacity onPress={onSharePress} style={styles.menuButton}>
            <Icon name="share-variant" size={28} color={useDarkTheme ? '#FFFFFF' : '#0E0E0E'} />
          </TouchableOpacity>
        )}
        {!isDashboard && onLogout && (
          <TouchableOpacity onPress={onLogout} style={styles.menuButton}>
            <Icon name="logout" size={28} color={useDarkTheme ? '#FFFFFF' : '#0E0E0E'} />
          </TouchableOpacity>
        )}
        {navLinks && navLinks.length > 0 && (
          <TouchableOpacity onPress={() => setMenuVisible(true)} style={styles.menuButton}>
            <Icon name="menu" size={24} color={useDarkTheme ? '#FFFFFF' : '#0E0E0E'} />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  return (
    <>
      <View style={containerStyles}>
        {Platform.OS === 'ios' ? (
          <BlurView intensity={useDarkTheme ? 20 : 80} tint={useDarkTheme ? 'dark' : 'light'} style={StyleSheet.absoluteFill}>
            <HeaderContent />
          </BlurView>
        ) : (
          <HeaderContent />
        )}
      </View>
      {navLinks && navLinks.length > 0 && (
        <Modal
          animationType="fade"
          transparent={true}
          visible={menuVisible}
          onRequestClose={() => setMenuVisible(false)}
        >
          <TouchableOpacity style={styles.modalOverlay} onPress={() => setMenuVisible(false)}>
            <View style={styles.menuContainer}>
              {navLinks.map((link, index) => (
                <TouchableOpacity
                  key={index}
                  style={styles.menuItem}
                  onPress={() => handlePress(link)}
                >
                  <Text style={styles.menuItemText}>{link.title}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </TouchableOpacity>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  header: {
    height: 58,
    justifyContent: 'flex-end',
    paddingBottom: 12,
  },
  androidHeader: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  // Dashboard variant - dark theme
  dashboardHeader: {
    height: 72,
    justifyContent: 'center',
  },
  androidDashboardHeader: {
    backgroundColor: 'rgba(4, 22, 53, 0.85)',
    // Web backdrop-filter for blur effect
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(8px)',
    }),
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  rightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuButton: {
    padding: 4,
    marginLeft: 10,
  },
  addCarButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#144CB8',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    marginLeft: 10,
    gap: 4,
  },
  addCarButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
    color: '#FFFFFF',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  menuContainer: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 8,
    marginRight: 10,
    marginTop: 80, // Adjust this to position the menu below the header
    minWidth: 200,
  },
  menuItem: {
    paddingVertical: 15,
  },
  menuItemText: {
    fontSize: 18,
    fontFamily: 'Satoshi-Medium',
  },
});

export default MobileHeader;
